import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { OrphanLaunch, RunSummary } from "@ideacrunch/shared";
import { useEngineAuth, useOrphanLaunches, useRuns } from "../api";
import { relativeTime, Sparkline, statusLabel } from "../components/bits";

/** A launch the engine never carried to Intake (killed by a restart or a
 *  crash before the run folder existed). The staged batch still holds the
 *  full prompt and uploads, so the user can relaunch or discard. */
function OrphanLaunchCard({
  launch,
  onRelaunched,
}: {
  launch: OrphanLaunch;
  onRelaunched: () => void;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<"relaunch" | "discard" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: "relaunch" | "discard") => {
    setBusy(action);
    setError(null);
    try {
      const res = await fetch(
        `/api/launches/${encodeURIComponent(launch.batchId)}` +
          (action === "relaunch" ? "/relaunch" : ""),
        { method: action === "relaunch" ? "POST" : "DELETE" },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      }
      void queryClient.invalidateQueries({ queryKey: ["launches"] });
      if (action === "relaunch") onRelaunched();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(null);
    }
  };

  return (
    <div className="run-card attention">
      <div className="title">{launch.ideaPreview}</div>
      <div className="sub">
        <span className="chip warn">interrupted before intake</span>
        {launch.depth && <span className="chip ghost">{launch.depth}</span>}
        {launch.uploadNames.length > 0 && (
          <span className="chip ghost">
            {launch.uploadNames.length} file
            {launch.uploadNames.length === 1 ? "" : "s"}
          </span>
        )}
        <span>{relativeTime(launch.launchedAt)}</span>
      </div>
      <div className="sub">
        The engine was stopped before it wrote anything for this idea. Your
        prompt and files are intact - relaunch to run it, or discard.
      </div>
      {launch.failureNote && (
        <div className="sub">
          Engine's last words: &quot;{launch.failureNote}&quot;
        </div>
      )}
      {error && <div className="error-box">{error}</div>}
      <div className="sub" style={{ display: "flex", gap: 8 }}>
        <button
          className="btn"
          disabled={busy !== null}
          onClick={() => void act("relaunch")}
        >
          {busy === "relaunch" ? "Relaunching..." : "Relaunch"}
        </button>
        <button
          className="btn secondary"
          disabled={busy !== null}
          onClick={() => {
            if (
              window.confirm(
                "Discard this interrupted launch? Its staged prompt and files are deleted.",
              )
            ) {
              void act("discard");
            }
          }}
        >
          Discard
        </button>
      </div>
    </div>
  );
}

function RunCard({ run }: { run: RunSummary }) {
  const attention = run.pendingGate !== null;
  const verdictToken = run.verdict?.split(/[\s(]/)[0] ?? null;
  return (
    <Link to={`/runs/${run.id}`} className={`run-card ${attention ? "attention" : ""}`}>
      <div className="title">{run.title}</div>
      <Sparkline run={run} />
      <div className="sub">
        {attention ? (
          <span className="chip warn">{statusLabel(run)}</span>
        ) : (
          <span
            className={`chip ${
              run.status === "killed"
                ? "critical"
                : verdictToken === "PURSUE"
                  ? "good"
                  : "ghost"
            }`}
          >
            {run.status === "complete" && verdictToken
              ? verdictToken
              : statusLabel(run)}
          </span>
        )}
        {run.depth !== "unknown" && (
          <span className="chip ghost">{run.depth}</span>
        )}
        <span>{relativeTime(run.lastActivity)}</span>
      </div>
    </Link>
  );
}

export function Home() {
  const { data: runs, isLoading, error } = useRuns();
  const { data: orphanLaunches } = useOrphanLaunches();
  const { data: engineAuth } = useEngineAuth();
  const [relaunched, setRelaunched] = useState(false);
  const location = useLocation();
  const justLaunched =
    relaunched ||
    (location.state as { justLaunched?: boolean } | null)?.justLaunched === true;

  if (isLoading) return <div className="loading">Loading runs...</div>;
  if (error) return <div className="error-box">{String(error)}</div>;

  const needsCall = (runs ?? []).filter((r) => r.pendingGate !== null);
  const inProgress = (runs ?? []).filter(
    (r) => r.pendingGate === null && r.status === "in-progress",
  );
  const finished = (runs ?? []).filter(
    (r) => r.status === "complete" || r.status === "killed",
  );

  return (
    <div>
      {engineAuth?.state === "expired" && (
        <div className="error-box" style={{ marginBottom: 14 }}>
          The server&apos;s Claude sign-in has expired - runs cannot start or
          resume.{" "}
          {engineAuth.manage ? (
            <Link to="/settings">Fix it in Settings &gt; Engine authentication.</Link>
          ) : (
            <>Ask the owner to re-authenticate the engine.</>
          )}
        </div>
      )}
      {justLaunched && (
        <div className="gate-banner" style={{ marginBottom: 14 }}>
          <div className="verdict-line">
            Run starting: the engine is framing your idea. Its card appears
            here as soon as intake writes the first artifact (a minute or
            two), and the Gate 0 diamond lights up when it needs your call.
          </div>
        </div>
      )}
      {(orphanLaunches?.length ?? 0) > 0 && (
        <>
          <div className="section-label attention">
            Interrupted before intake ({orphanLaunches?.length})
          </div>
          <div className="card-grid">
            {orphanLaunches?.map((l) => (
              <OrphanLaunchCard
                key={l.batchId}
                launch={l}
                onRelaunched={() => setRelaunched(true)}
              />
            ))}
          </div>
        </>
      )}
      {needsCall.length > 0 && (
        <>
          <div className="section-label attention">
            Needs your call ({needsCall.length})
          </div>
          <div className="card-grid">
            {needsCall.map((r) => (
              <RunCard key={r.id} run={r} />
            ))}
          </div>
        </>
      )}

      {inProgress.length > 0 && (
        <>
          <div className="section-label">In progress ({inProgress.length})</div>
          <div className="card-grid">
            {inProgress.map((r) => (
              <RunCard key={r.id} run={r} />
            ))}
          </div>
        </>
      )}

      <div className="section-label">All ideas ({runs?.length ?? 0})</div>
      {runs && runs.length > 0 ? (
        <div className="card-grid">
          {finished.map((r) => (
            <RunCard key={r.id} run={r} />
          ))}
          {finished.length === 0 && needsCall.length === 0 && inProgress.length === 0 && (
            <div className="empty-state">No finished runs yet.</div>
          )}
        </div>
      ) : (
        <div className="empty-state">
          <p>
            <strong>No runs yet.</strong>
          </p>
          <p>
            Your idea gets stress-tested, not cheered on: parallel researchers
            gather evidence, an independent Skeptic tries to break every claim,
            and you make the calls at three stop points.
          </p>
        </div>
      )}
    </div>
  );
}
